<template>
  <div class="app-container edi-orders-page">
    <div class="statistics-container">
      <div class="statistic-card card-danger">
        <div class="statistic-value" style="color:#f56c6c">{{ topData.exceptionCount || 0 }}</div>
        <div class="statistic-label">{{ $t('ExternalOrders.EDI.summaryException') }}</div>
      </div>
      <div class="statistic-card card-primary">
        <div class="statistic-value">{{ topData.customerActionCount || 0 }}</div>
        <div class="statistic-label">{{ $t('ExternalOrders.EDI.summaryAwaitingClientApproval') }}</div>
      </div>
      <div class="statistic-card card-success">
        <div class="statistic-value">{{ topData.inProgressCount || 0 }}</div>
        <div class="statistic-label">{{ $t('ExternalOrders.EDI.summaryInProgress') }}</div>
      </div>
      <div class="statistic-card card-gray">
        <div class="statistic-value">{{ topData.completedCount || 0 }}</div>
        <div class="statistic-label">{{ $t('ExternalOrders.EDI.summaryComplete') }}</div>
      </div>
    </div>

    <BjPage
      ref="BjPage"
      :serial="false"
      :selection="false"
      :auto-fetch="false"
      :col-span="colSpan"
      :get-action="queryApi"
      :query-params="queryParams"
      :before-reset-search="beforeResetSearch"
      @showSetting="SortableTable.show"
      @header-dragend="SortableTable.handleHeaderDragend"
      @header-contextmenu="SortableTable.handleHeaderContextmenu"
      @afterFetchOriginalData="afterFetchOriginalData"
    >
      <template v-slot:baseForm>
        <el-col :span="colSpan">
          <el-form-item :label="$t('ExternalOrders.EDI.filterOrderNo')" prop="orderNo">
            <BjInput v-model="queryParams.orderNo" :placeholder="$t('ExternalOrders.EDI.filterOrderNo')" @keyup.enter.native="onView" />
          </el-form-item>
        </el-col>
        <el-col :span="colSpan">
          <el-form-item :label="$t('ExternalOrders.EDI.filterStore')" prop="code">
            <BjSelectLov v-model="queryParams.code" :placeholder="$t('ExternalOrders.EDI.filterStore')" :option-list="storeList" :clearable="false" @change="handleStoreChange" />
          </el-form-item>
        </el-col>
        <el-col :span="colSpan">
          <el-form-item :label="$t('ExternalOrders.EDI.filterRetailer')" prop="retailer">
            <BjSelectLov v-model="queryParams.retailer" :placeholder="$t('ExternalOrders.EDI.filterRetailer')" :option-list="retailerList" />
          </el-form-item>
        </el-col>
        <el-col :span="colSpan">
          <el-form-item :label="$t('ExternalOrders.EDI.filterEdiStatus')" prop="filter">
            <BjSelectLov v-model="queryParams.filter" :placeholder="$t('ExternalOrders.EDI.filterEdiStatus')" :option-list="filterList" />
          </el-form-item>
        </el-col>
        <el-col :span="colSpan">
          <el-form-item :label="$t('ExternalOrders.EDI.filterActionOwner')" prop="actionOwner">
            <BjSelectLov v-model="queryParams.actionOwner" :placeholder="$t('ExternalOrders.EDI.filterActionOwner')" :option-list="actionOwnerList" />
          </el-form-item>
        </el-col>
      </template>

      <template v-slot:tableColumn>
        <template v-for="(col, index) in dropCols">
          <el-table-column v-if="col.prop !== 'bjTableAction'" :key="`col_${index}_${col.prop}_${col.width || 'auto'}`" :prop="col.prop" :label="col.label" :width="col.width || 'auto'" :fixed="col.fixed || false" align="center" class-name="item">
            <template slot-scope="scope">
              <template v-if="col.prop == 'orderNo'">{{ scope.row.orderNo }}</template>
              <template v-else-if="col.prop == 'statusLabel'">
                <el-tooltip
                  v-if="scope.row.statusLabel === 'exception'"
                  :content="scope.row.statusDesc || $t(filterList.bjGet(scope.row.statusLabel))"
                  placement="top"
                >
                  <span class="edi-status-exception">{{ $t(filterList.bjGet(scope.row.statusLabel)) }}</span>
                </el-tooltip>
                <span v-else>{{ $t(filterList.bjGet(scope.row.statusLabel)) }}</span>
              </template>
              <span v-else>{{ scope.row[col.prop] | timeFilters(scope.row[col.prop]) }}</span>
            </template>
          </el-table-column>
          <el-table-column v-if="col.prop === 'bjTableAction'" :key="`col_${index}_${col.prop}_${col.width || 'auto'}`" :prop="col.prop" :label="col.label" :width="col.width || 'auto'" :fixed="col.fixed || 'right'" align="center">
            <template slot-scope="props">
              <el-button type="text" class="edit-btn" @click="onView(props.row)">{{ $t('viewText') }}</el-button>
              <el-button :disabled="props.row.statusLabel != 'exception'" type="text" class="delete-btn" @click="onRetry(props.row)">{{ $t('ExternalOrders.page.retryBtn') }}</el-button>
              <el-button :disabled="!props.row.isShowApproveBtn" type="text" class="delete-btn" @click="onApprove(props.row)">{{ $t('billingPage.DEPAppro') }}</el-button>
            </template>
          </el-table-column>
        </template>
      </template>
    </BjPage>
    <EdiDetailDrawer ref="EdiDetailDrawer" @refresh="reFresh" />
  </div>
</template>

<script>
import pageMiXin from '@/utils/page-mixin.js';
import { channelApi, waveApi } from '@/api';
import EdiDetailDrawer from './components/DetailDrawer.vue';

export default {
  name: 'BusinessChannelEDIOrders',
  components: {
    EdiDetailDrawer,
  },
  mixins: [pageMiXin],
  data() {
    return {
      colSpan: 4,
      queryApi: waveApi.getEDIPageList,
      queryParams: {
        orderNo: null,
        code: null,
        retailer: null,
        filter: null,
        actionOwner: null,
      },
      storeList: [],
      retailerList: [],
      filterList: [
        { label: this.$t('ExternalOrders.EDI.summaryException'), value: 'exception' },
        { label: this.$t('ExternalOrders.EDI.summaryAwaitingClientApproval'), value: 'pending' },
        { label: this.$t('ExternalOrders.EDI.summaryInProgress'), value: 'inprogress' },
        { label: this.$t('ExternalOrders.EDI.summaryComplete'), value: 'completed' },
      ],
      actionOwnerList: [
        { label: 'Client', value: 'CLIENT' },
        { label: 'CSG', value: 'CSG' },
      ],
      labelList1: [
        { label: 'ExternalOrders.EDI.columnOrderNo', prop: 'orderNo', isCheck: true, disabled: false },
        { label: 'ExternalOrders.EDI.columnRetailer', prop: 'retailer', isCheck: true, disabled: false },
        { label: 'ExternalOrders.EDI.columnCustomer', prop: 'tenantName', isCheck: true, disabled: false },
        { label: 'ExternalOrders.EDI.columnStore', prop: 'erpStoreName', isCheck: true, disabled: false },
        { label: 'ExternalOrders.EDI.columnEdiStatus', prop: 'statusLabel', isCheck: true, disabled: false },
        { label: 'ExternalOrders.EDI.columnActionOwner', prop: 'actionOwner', isCheck: true, disabled: false },
        { label: 'ExternalOrders.EDI.columnReceived', prop: 'createdDate', isCheck: true, disabled: false },
        { label: 'ExternalOrders.EDI.columnAction', prop: 'bjTableAction', isCheck: true, disabled: false, width: 161 },
      ],
      topData: {},
    };
  },
  computed: {
    labelList() {
      return this.$store.getters.cusId ? this.labelList1.filter(item => item.prop != 'tenantName') : this.labelList1;
    },
  },
  created() {
    this.getStoreList();
  },
  methods: {
    getStoreList() {
      channelApi.getAllShopPage({
        page: 0,
        size: 99,
        channelTypeCode: '23',
      }).then(res => {
        this.storeList = (res.content || []).map(item => ({
          label: item.name,
          value: item.code,
        }));
        if (this.storeList.length) {
          this.queryParams.code = this.storeList[0].value;
          this.handleStoreChange(this.queryParams.code);
        }
      });
    },
    getRetailers(code) {
      waveApi.getEDIPageRetailers({ code }).then(res => {
        if (res.length > 0) {
          this.retailerList = (res || []).map(item => {
            return { label: item.name, value: item.gsCode };
          });
        } else {
          this.retailerList = [];
        }
      });
    },
    beforeResetSearch() {
      this.queryParams.code = this.storeList[0]?.value || null;
      return Promise.resolve();
    },
    handleStoreChange(code) {
      if (!code) return;
      this.$nextTick(() => {
        this.$refs.BjPage && this.$refs.BjPage.fetchData(1);
        this.getRetailers(code);
      });
    },
    onView(row) {
      this.$refs.EdiDetailDrawer.show(row);
    },
    onRetry(row) {
      waveApi.retryEDIPageData({ code: row.erpStoreCode, id: row.docId, status: 'ACCEPTED' }).then(res => {
        if (!res.failed) {
          this.$message.success(this.$t('toast.operationSuccessText'));
          setTimeout(() => {
            this.reFresh();
          }, 500);
        }
      });
    },
    onApprove(row) {
      this.$confirm(this.$t('toast.operationText'), this.$t('toast.hintText'), {
        confirmButtonText: this.$t('toast.okText'),
        cancelButtonText: this.$t('toast.cancelText'),
        type: 'warning',
      }).then(() => {
        waveApi.EDIPageApprove({
          code: row.erpStoreCode,
          id: row.docId,
        }).then(res => {
          if (res.orderStatus === 'CSG in progress') {
            this.$message.success(this.$t('toast.operationSuccessText'));
            setTimeout(() => {
              this.reFresh();
            }, 500);
          }
        });
      }).catch(() => {});
    },
    /** 处理返回数据格式 */
    afterFetchOriginalData(res) {
      // 优化列表页数据
      if (res) {
        res.content = res.records || [];
        res.totalElements = Number(res.total) || 0;
        res.totalPages = Number(res.pages) || 0;
      }
      this.getTopCardData(this.queryParams);
    },
    getTopCardData(queryParams) {
      if (!queryParams.code) return;
      waveApi.getEDIPageCard({ code: queryParams.code }).then(res => {
        this.topData = res || {};
      });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .bj-page .el-form-item__label {
  display: none;
}

.edi-status-exception {
  color: #f56c6c;
  cursor: pointer;
}

.statistics-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  .statistic-card {
    background: #fff;
    border-radius: 4px;
    padding: 20px;
    border-left: 4px solid #dcdfe6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .statistic-value {
      font-size: 28px;
      font-weight: bold;
      color: #303133;
      margin-bottom: 8px;
    }

    .statistic-label {
      font-size: 14px;
      color: #909399;
    }

    &.card-danger {
      border-left-color: #f56c6c;
    }

    &.card-primary {
      border-left-color: #f59e0b;
    }

    &.card-success {
      border-left-color: #409eff;
    }

    &.card-gray {
      border-left-color: #67c23a;
    }
  }
}

</style>
